import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

function ProgressBar({ pct }) {
    const capped = Math.min(pct, 100);
    const label = pct > 100 ? '100+%' : `${pct}%`;
    return (
        <div style={styles.progressWrap}>
            <div style={styles.progressTrack}>
                <div style={{ ...styles.progressFill, width: `${capped}%` }} />
            </div>
            <span style={styles.progressLabel}>{label}</span>
        </div>
    );
}

function ProjectList() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');

    useEffect(() => {
        document.title = 'SHARC - Projects';
        axios.get(`${config.API_URL}/projects`)
            .then(res => {
                setProjects(res.data);
                setLoading(false);
            })
            .catch(() => {
                setError('Failed to load projects.');
                setLoading(false);
            });
        return () => { document.title = 'SHARC - Shows Archive'; };
    }, []);

    const q = search.trim().toLowerCase();
    const filtered = q
        ? projects.filter(p =>
            p.name.toLowerCase().includes(q) ||
            (p.entity_name && p.entity_name.toLowerCase().includes(q))
          )
        : projects;

    const formatDate = (dt) => {
        if (!dt) return '—';
        return new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
    };

    const entityPath = (p) => {
        if (!p.entity_slug && !p.entity_id) return null;
        const map = { artist: 'artists', venue: 'venues', promoter: 'promoters', member: 'members' };
        return `/${map[p.entity_type]}/${p.entity_slug || p.entity_id}`;
    };

    if (loading) return <div style={styles.message}>Loading projects...</div>;
    if (error) return <div style={styles.error}>{error}</div>;

    return (
        <div>
            <div style={styles.topBar}>
                <h1 style={styles.title}>Projects</h1>
                <Link to="/add-project" style={styles.addButton}>+ Add Project</Link>
            </div>

            <div style={styles.searchWrap}>
                <input
                    type="text"
                    placeholder="Search by project name or entity name..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={styles.searchInput}
                />
            </div>

            {filtered.length === 0 ? (
                <div style={styles.emptyState}>
                    {q ? 'No projects match your search.' : 'No projects yet. Be the first to start one!'}
                    {!q && <div style={{ marginTop: '16px' }}><Link to="/add-project" style={styles.addButton}>+ Add Project</Link></div>}
                </div>
            ) : (
                <div style={styles.tableContainer}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Project Name</th>
                                <th style={styles.th}>Entity Type</th>
                                <th style={styles.th}>Entity</th>
                                <th style={styles.th}>Progress</th>
                                <th style={styles.th}>Created By</th>
                                <th style={styles.th}>Replies</th>
                                <th style={styles.th}>Last Reply By</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((p, idx) => {
                                const ePath = entityPath(p);
                                return (
                                    <tr key={p.id} style={idx % 2 === 0 ? styles.trEven : styles.trOdd}>
                                        <td style={styles.td}>
                                            <Link to={`/projects/${p.id}`} style={styles.projectLink}>{p.name}</Link>
                                        </td>
                                        <td style={{ ...styles.td, textTransform: 'capitalize', color: '#aaa' }}>
                                            {p.entity_type}
                                        </td>
                                        <td style={styles.td}>
                                            {ePath
                                                ? <Link to={ePath} style={styles.tableLink}>{p.entity_name}</Link>
                                                : <span style={{ color: '#666' }}>{p.entity_name || '—'}</span>
                                            }
                                        </td>
                                        <td style={{ ...styles.td, minWidth: '180px' }}>
                                            <ProgressBar pct={p.pct_complete} />
                                            <div style={styles.progressSubtext}>{p.actual_value} / {p.estimated_value} shows</div>
                                        </td>
                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                            {p.created_by_display_name || '—'}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center', color: '#aaa' }}>
                                            {p.reply_count || 0}
                                        </td>
                                        <td style={{ ...styles.td, color: '#aaa', fontSize: '13px' }}>
                                            {p.last_reply_by_display_name
                                                ? <><span>{p.last_reply_by_display_name}</span><br /><span style={{ fontSize: '11px', color: '#555' }}>{p.last_reply_at ? formatDate(p.last_reply_at) : ''}</span></>
                                                : '—'
                                            }
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

const styles = {
    topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', margin: 0 },
    addButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '6px', padding: '10px 20px', fontSize: '14px', fontWeight: 'bold', textDecoration: 'none', cursor: 'pointer' },
    searchWrap: { marginBottom: '20px' },
    searchInput: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px', padding: '10px 14px', fontSize: '14px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    tableContainer: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { textAlign: 'left', padding: '10px 16px', color: '#aaa', fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #333', whiteSpace: 'nowrap' },
    td: { padding: '12px 16px', color: '#e0e0e0', borderBottom: '1px solid #222', verticalAlign: 'middle' },
    trEven: { backgroundColor: '#1e1e1e' },
    trOdd: { backgroundColor: '#1a1a1a' },
    projectLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: '600' },
    tableLink: { color: '#bb86fc', textDecoration: 'none' },
    progressWrap: { display: 'flex', alignItems: 'center', gap: '8px' },
    progressTrack: { flex: 1, height: '8px', backgroundColor: '#2c2c2c', borderRadius: '4px', overflow: 'hidden', minWidth: '80px' },
    progressFill: { height: '100%', backgroundColor: '#bb86fc', borderRadius: '4px', transition: 'width 0.3s ease' },
    progressLabel: { fontSize: '12px', color: '#bb86fc', fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: '40px' },
    progressSubtext: { fontSize: '11px', color: '#555', marginTop: '3px' },
    emptyState: { textAlign: 'center', color: '#666', padding: '60px 0', fontSize: '15px' },
    message: { textAlign: 'center', color: '#aaa', fontSize: '16px', padding: '60px 0' },
    error: { textAlign: 'center', color: '#cf6679', fontSize: '16px', padding: '60px 0' },
};

export default ProjectList;
